import { ReactNode, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dropdown, Menu } from 'antd';
import { TbCube3dSphere } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { taskService } from '@/services';
import { DropdownMenuLabel, DropdownOptionContent, DropdownPanel, DropdownPanelMenu, EmptySection } from '@/shared';
import {
    useDashboardStore,
    useFolderStore,
    useInvestmentStore,
    useListStore,
    useMemoStore,
    usePipelineStore,
    useReportStore,
    useTeamStore,
} from '@/stores';
import { SetStateMethod } from '@/types';
import { formatDate } from '@/utilities';

export type WorkspaceResourceType =
    'Dashboard' | 'Folder' | 'Investment' | 'List' | 'Memo' | 'Pipeline' | 'Report' | 'Task' | 'Team';

interface WorkspaceResourceOption {
    id: string;
    label: string;
    resource: object;
    type: WorkspaceResourceType;
    listType?: string;
    updatedAt?: Date;
    website?: string;
}

interface WorkspaceResourceDropdownProps<T extends { id?: string }> {
    children: ReactNode;
    excludeResourceIds?: string[];
    resource: T;
    resourceTypes?: WorkspaceResourceType[];
    setResource: SetStateMethod<T>;
}

const normalizeResource = (resource: object, type: WorkspaceResourceType): WorkspaceResourceOption => {
    const record = resource as Record<string, unknown>;
    const labelKeys = ['name', 'title', 'editedName', 'symbol'];
    const label = labelKeys.map((key) => record[key]).find((value): value is string => typeof value === 'string');
    const updatedAt =
        record.updatedAt instanceof Date
            ? record.updatedAt
            : typeof record.updatedAt === 'string'
              ? new Date(record.updatedAt)
              : undefined;

    return {
        id: String(record.id),
        label: label ?? 'Untitled',
        listType: type === 'List' && typeof record.type === 'string' ? record.type : undefined,
        resource,
        type,
        updatedAt,
        website: typeof record.website === 'string' ? record.website : undefined,
    };
};

const WorkspaceResourceDropdown = <T extends { id?: string }>({
    children,
    excludeResourceIds = [],
    resource,
    resourceTypes,
    setResource,
}: WorkspaceResourceDropdownProps<T>): JSX.Element => {
    const [input, setInput] = useState('');

    const { token } = useAntDesignTheme();

    // Resources
    const { investments } = useInvestmentStore();
    const { memos } = useMemoStore();
    const { pipelines } = usePipelineStore();
    const { folders } = useFolderStore();
    const { teams } = useTeamStore();
    // Relations
    const { dashboards } = useDashboardStore();
    const { lists } = useListStore();
    const { reports } = useReportStore();
    const includeTasks = resourceTypes?.includes('Task') ?? false;
    const { data: taskBuckets } = useQuery({
        queryKey: ['tasks', 'workspace-resource-dropdown'],
        queryFn: () => taskService.listBuckets(),
        enabled: includeTasks,
        staleTime: 60_000,
    });

    // Combine everything into a single list with type metadata
    const workspaceResources = useMemo(() => {
        const allResources = [
            ...folders.map((item) => normalizeResource(item, 'Folder')),
            ...investments.map((item) => normalizeResource(item, 'Investment')),
            ...memos.map((item) => normalizeResource(item, 'Memo')),
            ...pipelines.map((item) => normalizeResource(item, 'Pipeline')),
            ...teams.map((item) => normalizeResource(item, 'Team')),
            ...dashboards.map((item) => normalizeResource(item, 'Dashboard')),
            ...lists.map((item) => normalizeResource(item, 'List')),
            ...reports.map((item) => normalizeResource(item, 'Report')),
            ...Object.values(taskBuckets ?? {})
                .flat()
                .map((item) => normalizeResource(item, 'Task')),
        ];

        return allResources.filter(
            (item) => (!resourceTypes || resourceTypes.includes(item.type)) && !excludeResourceIds.includes(item.id),
        );
    }, [
        dashboards,
        excludeResourceIds,
        folders,
        investments,
        lists,
        memos,
        pipelines,
        reports,
        resourceTypes,
        taskBuckets,
        teams,
    ]);

    // Search filtering
    const searchedResources = useMemo(() => {
        if (!input) {
            return workspaceResources;
        }

        const lower = input.toLowerCase();

        return workspaceResources.filter((item) => item.label.toLowerCase().includes(lower));
    }, [input, workspaceResources]);

    // Group results by type
    const groupedResources = useMemo(() => {
        return searchedResources.reduce<Partial<Record<WorkspaceResourceType, WorkspaceResourceOption[]>>>(
            (groups, item) => {
                if (!groups[item.type]) {
                    groups[item.type] = [];
                }

                groups[item.type]?.push(item);

                return groups;
            },
            {},
        );
    }, [searchedResources]);

    const sectionOrder: WorkspaceResourceType[] = [
        'Folder',
        'Investment',
        'Memo',
        'Pipeline',
        'Task',
        'Team',
        'Dashboard',
        'Report',
        'List',
    ];
    const sectionLabels: Record<WorkspaceResourceType, string> = {
        Dashboard: 'Dashboards',
        Folder: 'Folders',
        Investment: 'Investments',
        List: 'Lists',
        Memo: 'Memos',
        Pipeline: 'Pipelines',
        Report: 'Reports',
        Task: 'Tasks',
        Team: 'Teams',
    };

    return (
        <Dropdown
            trigger={['click']}
            popupRender={() => (
                <DropdownPanel
                    maxHeight={16 * 20}
                    search={{
                        placeholder: 'Search Workspace...',
                        setValue: setInput,
                        value: input,
                    }}
                >
                    {searchedResources.length > 0 ? (
                        sectionOrder.map((type) => {
                            const items = groupedResources[type];
                            if (!items || items.length === 0) {
                                return null;
                            }

                            return (
                                <div key={type}>
                                    <DropdownMenuLabel>{sectionLabels[type]}</DropdownMenuLabel>
                                    <DropdownPanelMenu
                                        selectable={false}
                                        onClick={({ key }) => {
                                            const selected = searchedResources.find((item) => item.id === key);

                                            if (selected) {
                                                setResource(selected.resource as T);
                                            }

                                            setInput('');
                                        }}
                                    >
                                        {items.map((r) => (
                                            <Menu.Item key={r.id}>
                                                <DropdownOptionContent
                                                    avatar={{
                                                        listType: r.listType,
                                                        resource: r.type.toLowerCase(),
                                                        website: r.type === 'Investment' ? r.website : undefined,
                                                    }}
                                                    metadata={[
                                                        r.updatedAt ? `Last updated ${formatDate(r.updatedAt)}` : null,
                                                        r.type,
                                                    ]}
                                                    selected={resource?.id === r.id}
                                                    title={r.label}
                                                />
                                            </Menu.Item>
                                        ))}
                                    </DropdownPanelMenu>
                                </div>
                            );
                        })
                    ) : (
                        <EmptySection
                            height={16 * 16}
                            icon={<TbCube3dSphere size='75%' style={{ color: token.colorTextTertiary }} />}
                            label='No Workspace Resources'
                            description={
                                input
                                    ? 'No workspace resources match your search.'
                                    : 'Workspace resources will appear here once they are created.'
                            }
                        />
                    )}
                </DropdownPanel>
            )}
        >
            {children}
        </Dropdown>
    );
};

export default WorkspaceResourceDropdown;
